import * as React from "react";
import ReactSelect, {
  components,
  ControlProps,
  IndicatorProps,
  OptionProps,
  OptionTypeBase,
} from "react-select";
import { StylesConfig } from "react-select/src/styles";
import { cs } from "../../core/classSet";
import { useTabletMediaQuery } from "../../core/hooks/useMediaQuery";
import { ArrowIcon, industryIconMap } from "../Icons/Icons";
import styles from "./Select.module.css";

export type IOption = OptionTypeBase;

export const styleModifiers: StylesConfig<IOption, false> = {
  control: (base, { selectProps }) => {
    const { inHeader } = selectProps;
    return {
      ...base,
      border: 0,
      flexWrap: "wrap",
      paddingBottom: inHeader ? "4px" : "12px",
      paddingLeft: inHeader ? "16px" : "24px",
      paddingRight: inHeader ? "8px" : "16px",
      paddingTop: inHeader ? "8px" : "12px",
    };
  },
  dropdownIndicator: (base) => ({
    ...base,
    color: "#1887fc",
    ":hover": {
      color: "#1887fc",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    paddingTop: 0,
    marginTop: "8px",
    boxShadow: "0 0 16px rgba(0, 30, 64, 0.1)",
  }),
  menuList: (base) => ({
    ...base,
    "::-webkit-scrollbar": {
      width: "6px",
    },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: "unset",
    },
    ":hover::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      borderRadius: "4px",
    },
  }),
  option: (base, { data, isSelected }) => ({
    ...base,
    alignItems: "center",
    backgroundColor: "#ffffff",
    ":hover": {
      backgroundColor: "#fafdff",
    },
    borderTop: data.value !== "all" ? "1px solid #eceff2" : "",
    color: "#1a1a1a",
    display: "flex",
    fontSize: "20px",
    lineHeight: "1.5",
    fontWeight: isSelected ? 500 : 400,
    paddingLeft: "16px",
  }),
  singleValue: (base) => ({
    ...base,
    marginLeft: 0,
    marginRight: 0,
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: 0,
  }),
};

function CustomControl({ children, ...props }: ControlProps<IOption, false>) {
  const { label, inHeader } = props.selectProps;
  return (
    <components.Control {...props}>
      {!!label && <h4 className={cs(styles.label, inHeader && styles.labelHeader)}>{label}</h4>}
      {children}
    </components.Control>
  );
}

function CustomOption({ children, ...props }: OptionProps<IOption, false>) {
  const { label } = props.selectProps;
  const Icon = industryIconMap[props.data.value];
  return (
    <components.Option {...props}>
      {!!label && label === "Industry" && Icon && (
        <Icon className={styles.icon} width={16} height={16} />
      )}
      {children}
    </components.Option>
  );
}

function CustomDropdownIndicator(props: IndicatorProps<IOption, false>) {
  return (
    <components.DropdownIndicator {...props}>
      <ArrowIcon direction={props.selectProps.menuIsOpen ? "up" : "down"} width={16} height={16} />
    </components.DropdownIndicator>
  );
}

interface IProps {
  className?: string;
  defaultValue?: IOption;
  inHeader?: boolean;
  label?: string;
  options: IOption[];
  value?: string;
  onChange: (v: IOption | null) => any;
}

export default function Select({
  className,
  defaultValue,
  inHeader,
  label,
  options,
  value,
  onChange,
}: IProps) {
  const isTabletViewport = useTabletMediaQuery();

  function getValue() {
    const option = options.find((o) => o.value === value);
    return option ?? null;
  }

  // slightly different callback for use on mobile
  function handleChange(ev: React.ChangeEvent<HTMLSelectElement>) {
    const { value } = ev.target;
    onChange({ value });
  }

  // on tablet and above show the react-select "enhanced" version
  if (isTabletViewport) {
    return (
      <ReactSelect
        classNamePrefix={
          process.env.NODE_ENV === "development" ? "__DEV_SHOW_COMPONENT_NAMES" : undefined
        }
        className={className}
        closeMenuOnSelect={true}
        components={{
          Control: CustomControl,
          DropdownIndicator: CustomDropdownIndicator,
          Option: CustomOption,
        }}
        defaultValue={defaultValue}
        inHeader={inHeader}
        label={label}
        isMulti={false}
        isSearchable={false}
        options={options}
        styles={styleModifiers}
        value={getValue()}
        onChange={onChange}
      />
    );
  }

  return (
    <div className={styles.mobileRoot}>
      {!!label && (
        <label className={styles.mobileLabel} htmlFor={label}>
          {label}
        </label>
      )}
      <div className={styles.mobileInner}>
        <select
          className={cs(styles.mobileSelect, className)}
          id={label}
          value={value}
          onChange={handleChange}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className={styles.mobileIcon}>
          <ArrowIcon direction="down" width={16} height={16} />
        </div>
      </div>
    </div>
  );
}
